import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faShareNodes, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import {
  getCourseInspiration,
  getCoursePlanItems,
  getTeacherCourse,
  getTeacherCourseCohorts,
  getTeacherLessons,
  patchTeacherCourse,
  postCourseHeroImageUploadUrl,
  postSubmitCourseToCommunity,
  postUpgradeCourse,
  putCoursePlanOrder,
  removeTemplateLesson,
  deleteTeacherQuiz,
  type CoursePlanItem,
  type InspirationInfo,
  type TeacherCourseCohortRecord,
  type TeacherCourseRecord,
  type TeacherLessonRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { AddContentModal } from "./AddContentModal";
import { AddLessonModal } from "./AddLessonModal";
import { AddQuizModal } from "./AddQuizModal";
import CourseStandardsCoverage from "./CourseStandardsCoverage";
import { buildRemoveTemplateLessonConfirmMessage } from "./lessonDeleteMessages";
import { CoursePlanSequenceRail } from "./CoursePlanSequenceRail";
import { WhatsChangedModal } from "./WhatsChangedModal";
import { InspirationModal } from "./InspirationModal";
import { waitForMediaUrl } from "../media/waitForMediaUrl";

const HERO_MAX_BYTES = 5 * 1024 * 1024;
const HERO_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

export default function TeacherCourseLayout() {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const navigate = useNavigate();
  const [course, setCourse] = useState<TeacherCourseRecord | null>(null);
  const [lessons, setLessons] = useState<TeacherLessonRecord[]>([]);
  const [planItems, setPlanItems] = useState<CoursePlanItem[]>([]);
  const [templateCohorts, setTemplateCohorts] = useState<TeacherCourseCohortRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addContentOpen, setAddContentOpen] = useState(false);
  const [addLessonOpen, setAddLessonOpen] = useState(false);
  const [addQuizOpen, setAddQuizOpen] = useState(false);
  const [insertAfterPlanItemId, setInsertAfterPlanItemId] = useState<string | null | undefined>(undefined);
  const [focusStandard, setFocusStandard] = useState<{
    standardSetId: string;
    standardId: string;
    prompt?: string;
  } | null>(null);
  const [heroBusy, setHeroBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [pendingHeroKey, setPendingHeroKey] = useState<string | null>(null);
  const [heroPreviewOverride, setHeroPreviewOverride] = useState<string | null>(null);
  const [inspiration, setInspiration] = useState<{
    info: InspirationInfo | null;
    loading: boolean;
    upgrading: boolean;
  } | null>(null);
  const heroInputRef = useRef<HTMLInputElement>(null);
  const [pendingOrderIds, setPendingOrderIds] = useState<string[] | null>(null);
  const [orderBeforeIds, setOrderBeforeIds] = useState<string[]>([]);
  const [orderSaving, setOrderSaving] = useState(false);
  const [orderResetSignal, setOrderResetSignal] = useState(0);
  const [titleDraft, setTitleDraft] = useState("");
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [pendingDetails, setPendingDetails] = useState<{ title: string; description: string } | null>(null);

  const published =
    course?.communityPublishStatus === "approved" || course?.communityPublishStatus === "pending_review";

  const refresh = useCallback(async () => {
    if (!bearer || !courseId) return;
    setError(null);
    setLoading(true);
    try {
      const [ct, le, plan, ch] = await Promise.all([
        getTeacherCourse(bearer, courseId),
        getTeacherLessons(bearer, courseId, { limit: 200, offset: 0 }),
        getCoursePlanItems(bearer, courseId),
        getTeacherCourseCohorts(bearer, courseId),
      ]);
      setCourse(ct.course);
      setLessons(le.lessons);
      setPlanItems(plan.items);
      setTemplateCohorts(ch.cohorts);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load course");
      setCourse(null);
      setTemplateCohorts([]);
    } finally {
      setLoading(false);
    }
  }, [bearer, courseId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!course) return;
    setTitleDraft(course.title);
    setDescriptionDraft(course.description ?? "");
  }, [course?.id, course?.title, course?.description]);

  function lessonInsertAfterId(planItemId: string | null | undefined): string | null | undefined {
    if (planItemId === null) return null;
    if (planItemId === undefined) return undefined;
    const idx = planItems.findIndex((p) => p.planItemId === planItemId);
    if (idx < 0) return undefined;
    for (let i = idx; i >= 0; i--) {
      const item = planItems[i];
      if (item?.itemKind === "lesson") return item.lesson.id;
    }
    return null;
  }

  function openAddContentModal(insertAfter: string | null | undefined) {
    setInsertAfterPlanItemId(insertAfter);
    setFocusStandard(null);
    setAddContentOpen(true);
  }

  useEffect(() => {
    const addLesson = searchParams.get("addLesson");
    if (!addLesson || !courseId) return;
    if (addLesson === "start") openAddContentModal(null);
    else if (addLesson === "end") openAddContentModal(undefined);
    else if (addLesson.startsWith("after-plan-")) {
      openAddContentModal(addLesson.slice("after-plan-".length));
    } else if (addLesson.startsWith("after-")) {
      openAddContentModal(addLesson.slice("after-".length));
    }

    const standardSetId = searchParams.get("standardSetId")?.trim() ?? "";
    const standardId = searchParams.get("standardId")?.trim() ?? "";
    const standardPrompt = searchParams.get("standardPrompt")?.trim() ?? "";
    if (standardSetId && standardId) {
      setFocusStandard({
        standardSetId,
        standardId,
        ...(standardPrompt ? { prompt: standardPrompt } : {}),
      });
    }

    const next = new URLSearchParams(searchParams);
    next.delete("addLesson");
    next.delete("standardSetId");
    next.delete("standardId");
    next.delete("standardPrompt");
    setSearchParams(next, { replace: true });
  }, [courseId, searchParams, setSearchParams]);

  async function handleRemoveFromTemplate(lessonId: string, title: string) {
    if (!bearer || !courseId) return;
    if (!window.confirm(buildRemoveTemplateLessonConfirmMessage(title))) return;
    setError(null);
    try {
      await removeTemplateLesson(bearer, courseId, lessonId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Remove failed");
    }
  }

  async function handleRemoveQuiz(quizId: string, title: string) {
    if (!bearer || !courseId) return;
    const ok = window.confirm(
      `Delete quiz "${title}" from this course?\n\nThis removes it from the course plan permanently. Cohorts keep their own copy, so their students and existing results are not affected.`
    );
    if (!ok) return;
    setError(null);
    try {
      await deleteTeacherQuiz(bearer, courseId, quizId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete quiz failed");
    }
  }

  async function commitHero(key: string | null, changelog?: string) {
    if (!bearer || !courseId) return;
    setError(null);
    try {
      const { course: updated } = await patchTeacherCourse(bearer, courseId, {
        heroImageKey: key,
        ...(changelog ? { changelog } : {}),
      });
      setCourse(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update hero image");
    }
  }

  async function saveAccentColor(next: string | null) {
    if (!bearer || !courseId) return;
    setError(null);
    try {
      const { course: updated } = await patchTeacherCourse(bearer, courseId, { accentColor: next });
      setCourse(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update accent color");
    }
  }

  const detailsDirty =
    course != null &&
    (titleDraft.trim() !== course.title || descriptionDraft.trim() !== (course.description ?? "").trim());

  async function commitDetails(changelog?: string) {
    if (!bearer || !courseId) return;
    const title = titleDraft.trim();
    if (!title) {
      setError("Course title cannot be empty.");
      return;
    }
    setDetailsSaving(true);
    setError(null);
    try {
      const { course: updated } = await patchTeacherCourse(bearer, courseId, {
        title,
        description: descriptionDraft.trim(),
        ...(changelog ? { changelog } : {}),
      });
      setCourse(updated);
      setPendingDetails(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update course details");
    } finally {
      setDetailsSaving(false);
    }
  }

  function handleSaveDetails() {
    if (!detailsDirty) return;
    if (published) {
      setPendingDetails({
        title: titleDraft.trim(),
        description: descriptionDraft.trim(),
      });
      return;
    }
    void commitDetails();
  }

  async function handleHeroFile(file: File) {
    if (!bearer || !courseId || heroBusy) return;
    if (!HERO_TYPES.includes(file.type)) {
      setError("Hero image must be a PNG, JPEG, or WebP.");
      return;
    }
    if (file.size > HERO_MAX_BYTES) {
      setError("Hero image must be under 5 MB.");
      return;
    }
    setHeroBusy(true);
    setError(null);
    const localPreview = URL.createObjectURL(file);
    setHeroPreviewOverride(localPreview);
    try {
      const { uploadUrl, key, previewUrl } = await postCourseHeroImageUploadUrl(bearer, courseId, {
        contentType: file.type,
        sizeBytes: file.size,
      });
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error("Upload failed");
      if (previewUrl) {
        await waitForMediaUrl(previewUrl);
      }
      if (published) {
        setPendingHeroKey(key);
      } else {
        await commitHero(key);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setHeroBusy(false);
      URL.revokeObjectURL(localPreview);
      setHeroPreviewOverride(null);
      if (heroInputRef.current) heroInputRef.current.value = "";
    }
  }

  async function handleShare() {
    if (!bearer || !courseId || sharing) return;
    setSharing(true);
    setError(null);
    try {
      await postSubmitCourseToCommunity(bearer, courseId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Share failed");
    } finally {
      setSharing(false);
    }
  }

  async function openInspiration() {
    if (!bearer || !courseId) return;
    setInspiration({ info: null, loading: true, upgrading: false });
    try {
      const info = await getCourseInspiration(bearer, courseId);
      setInspiration({ info, loading: false, upgrading: false });
    } catch {
      setInspiration({ info: null, loading: false, upgrading: false });
    }
  }

  async function handleUpgrade() {
    if (!bearer || !courseId || !inspiration) return;
    setInspiration((s) => (s ? { ...s, upgrading: true } : s));
    try {
      await postUpgradeCourse(bearer, courseId);
      setInspiration(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upgrade failed");
      setInspiration((s) => (s ? { ...s, upgrading: false } : s));
    }
  }

  function orderSnapshot(ids: readonly string[]) {
    return {
      planItemIds: [...ids],
      items: ids.map((id, i) => {
        const item = planItems.find((p) => p.planItemId === id);
        const title =
          item?.itemKind === "lesson"
            ? item.lesson.title
            : item?.itemKind === "quiz"
              ? item.quiz.title
              : id;
        return { position: i + 1, id, title };
      }),
    };
  }

  function handlePendingOrderSave(nextOrder: readonly CoursePlanItem[]) {
    const nextIds = nextOrder.map((i) => i.planItemId);
    setOrderBeforeIds(planItems.map((i) => i.planItemId));
    setPendingOrderIds(nextIds);
  }

  async function commitOrder(changelog: string) {
    if (!bearer || !courseId || !pendingOrderIds) return;
    setOrderSaving(true);
    setError(null);
    try {
      await putCoursePlanOrder(bearer, courseId, pendingOrderIds, { changelog });
      setPendingOrderIds(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save order failed");
    } finally {
      setOrderSaving(false);
    }
  }

  function cancelPendingOrder() {
    setPendingOrderIds(null);
    setOrderResetSignal((n) => n + 1);
  }

  if (!courseId) {
    return (
      <div className="teacher-page">
        <p className="warn-banner">Missing course id.</p>
      </div>
    );
  }

  const archived = course?.status === "archived";
  const startCohortHref = `/teacher/cohorts?courseId=${encodeURIComponent(courseId)}&create=1`;
  const detailsHref = `/teacher/courses/${courseId}`;
  const heroPreviewSrc = heroPreviewOverride ?? course?.heroImageUrl ?? null;

  return (
    <div className="teacher-page teacher-page--template-sequence">
      <nav className="teacher-breadcrumb">
        <Link to="/teacher/courses">Courses</Link>
        <span aria-hidden> / </span>
        <Link to={detailsHref}>{course?.title ?? "…"}</Link>
        <span aria-hidden> / </span>
        <span>Manage</span>
      </nav>

      <header className="teacher-page-header">
        <div className="teacher-page-header-row">
          <div>
            <h1 className="teacher-page-title">{course?.title ?? "Course"}</h1>
            <p className="teacher-page-lede muted">
              {archived
                ? "Archived. Editing is blocked in the API."
                : "Edit settings, hero image, accent color, and the lesson sequence."}
            </p>
          </div>
          <Link to={detailsHref} className="btn secondary" style={{ textDecoration: "none" }}>
            View details
          </Link>
        </div>
        {!archived ? (
          <div className="teacher-actions-row" style={{ marginTop: "0.75rem", flexWrap: "wrap" }}>
            <Link to={startCohortHref} className="btn primary" style={{ textDecoration: "none" }}>
              Start cohort
            </Link>
            <Link to="/teacher/lessons" className="btn secondary" style={{ textDecoration: "none" }}>
              Edit lessons in library
            </Link>
            <button type="button" className="btn secondary" disabled={sharing} onClick={() => void handleShare()}>
              <FontAwesomeIcon icon={faShareNodes} /> {published ? "Update in community" : "Share to community"}
            </button>
            {course?.sourceCommunityCourseId ? (
              <button type="button" className="btn ghost" onClick={() => void openInspiration()}>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Inspiration
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      {!archived ? (
        <section className="teacher-panel teacher-panel--flush">
          <h2 className="teacher-panel-title">Course details</h2>
          <div className="teacher-pad">
            <form
              className="stack-form tight"
              onSubmit={(ev) => {
                ev.preventDefault();
                handleSaveDetails();
              }}
            >
              <label className="field">
                <span className="field-label">Title</span>
                <input
                  className="input"
                  value={titleDraft}
                  onChange={(ev) => setTitleDraft(ev.target.value)}
                  disabled={detailsSaving}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">Description</span>
                <textarea
                  className="input"
                  value={descriptionDraft}
                  onChange={(ev) => setDescriptionDraft(ev.target.value)}
                  disabled={detailsSaving}
                  rows={4}
                  placeholder="What students and teachers should know about this course"
                />
              </label>
              <div className="teacher-actions-row">
                <button
                  type="submit"
                  className="btn primary"
                  disabled={!detailsDirty || detailsSaving || !titleDraft.trim()}
                >
                  {detailsSaving ? "Saving…" : "Save details"}
                </button>
              </div>
            </form>
          </div>
        </section>
      ) : null}

      {!archived ? (
        <section className="teacher-panel teacher-panel--flush">
          <h2 className="teacher-panel-title">Hero image</h2>
          <div className="teacher-hero-uploader teacher-pad">
            {heroPreviewSrc ? (
              <img
                className="teacher-hero-preview"
                src={heroPreviewSrc}
                alt={`${course?.title ?? "Course"} hero`}
                crossOrigin={heroPreviewOverride ? undefined : "anonymous"}
              />
            ) : (
              <div className="teacher-hero-preview" aria-hidden />
            )}
            <div>
              <p className="muted small">
                Recommended 1280x800 (16:10). PNG, JPEG, or WebP up to 5 MB. We optimize and scale it for fast
                loading.
              </p>
              <div className="teacher-actions-row" style={{ marginTop: "0.5rem" }}>
                <button
                  type="button"
                  className="btn secondary"
                  disabled={heroBusy}
                  onClick={() => heroInputRef.current?.click()}
                >
                  <FontAwesomeIcon icon={faImage} /> {heroBusy ? "Uploading…" : "Upload hero image"}
                </button>
                {course?.heroImageKey ? (
                  <button type="button" className="btn ghost" disabled={heroBusy} onClick={() => void commitHero(null)}>
                    Remove
                  </button>
                ) : null}
              </div>
              <input
                ref={heroInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                onChange={(ev) => {
                  const f = ev.target.files?.[0];
                  if (f) void handleHeroFile(f);
                }}
              />
            </div>
          </div>
        </section>
      ) : null}

      {!archived ? (
        <section className="teacher-panel teacher-panel--flush">
          <h2 className="teacher-panel-title">Accent color</h2>
          <div className="teacher-pad teacher-accent-picker">
            <p className="muted small">
              Optional background for the course details page and card hover. Leave empty for the default look.
            </p>
            <div className="teacher-actions-row" style={{ marginTop: "0.5rem", alignItems: "center" }}>
              <input
                type="color"
                className="teacher-accent-swatch-input"
                value={course?.accentColor?.trim() || "#1b4d2e"}
                aria-label="Course accent color"
                onChange={(ev) => void saveAccentColor(ev.target.value)}
              />
              <button
                type="button"
                className="btn ghost"
                disabled={!course?.accentColor}
                onClick={() => void saveAccentColor(null)}
              >
                Clear (none)
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {!archived ? (
        <div className="teacher-callout-muted" role="note">
          <p>
            Sequence changes affect future unassigned lesson plans only. Edit lesson content anytime from{" "}
            <Link to="/teacher/lessons">My lessons</Link>.
          </p>
        </div>
      ) : null}

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      <section className="teacher-panel teacher-template-sequence-panel">
        <div className="teacher-template-sequence-header">
          <h2 className="teacher-panel-title">Course plan</h2>
        </div>

        <CoursePlanSequenceRail
          courseId={courseId}
          items={planItems}
          archived={archived}
          loading={loading}
          bearer={bearer}
          onOrderSaved={refresh}
          onError={setError}
          onRemoveLesson={(lessonId, title) => void handleRemoveFromTemplate(lessonId, title)}
          onRemoveQuiz={(quizId, title) => void handleRemoveQuiz(quizId, title)}
          onInsert={archived ? undefined : openAddContentModal}
          requireChangelog={published}
          onPendingOrderSave={handlePendingOrderSave}
          resetOrderSignal={orderResetSignal}
        />
      </section>

      <CourseStandardsCoverage
        courseId={courseId}
        bearer={bearer}
        archived={archived}
        lessonSequenceKey={lessons.map((l) => l.id).join(",")}
      />

      <section className="teacher-panel teacher-panel--flush teacher-template-cohorts-panel">
        <h2 className="teacher-panel-title">Used by cohorts</h2>
        {loading ? (
          <p className="muted teacher-pad">Loading…</p>
        ) : templateCohorts.length === 0 ? (
          <p className="muted teacher-pad">
            No cohorts use this template yet.{" "}
            {!archived ? (
              <Link to={startCohortHref}>Start a cohort</Link>
            ) : (
              "Unarchive the template to start new cohorts."
            )}
          </p>
        ) : (
          <div className="teacher-table-wrap">
            <table className="teacher-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {templateCohorts.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.label}</strong>
                    </td>
                    <td>
                      <span className={`badge ${c.status === "archived" ? "badge-muted" : ""}`}>{c.status}</span>
                    </td>
                    <td className="muted small">{new Date(c.updatedAt).toLocaleString()}</td>
                    <td className="teacher-table-actions">
                      <Link
                        to={`/teacher/cohorts/${c.id}`}
                        className="btn primary tight"
                        style={{ textDecoration: "none" }}
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AddContentModal
        open={addContentOpen}
        onClose={() => setAddContentOpen(false)}
        onChooseLesson={() => {
          setAddContentOpen(false);
          setAddLessonOpen(true);
        }}
        onChooseQuiz={() => {
          setAddContentOpen(false);
          setAddQuizOpen(true);
        }}
      />

      <AddLessonModal
        open={addLessonOpen}
        context="template"
        courseId={courseId}
        insertAfterId={lessonInsertAfterId(insertAfterPlanItemId)}
        insertAfterPlanItemId={insertAfterPlanItemId}
        sequenceLessons={lessons}
        focusStandard={focusStandard}
        onClose={() => {
          setAddLessonOpen(false);
          setFocusStandard(null);
        }}
        onAdded={() => refresh()}
      />

      <AddQuizModal
        open={addQuizOpen}
        context="template"
        courseId={courseId}
        insertAfterPlanItemId={insertAfterPlanItemId}
        planItems={planItems}
        onClose={() => {
          setAddQuizOpen(false);
          setInsertAfterPlanItemId(undefined);
        }}
        onAdded={() => refresh()}
      />

      <WhatsChangedModal
        open={pendingHeroKey !== null}
        kind="courses"
        title={course?.title ?? "course"}
        before={{ heroImageKey: course?.heroImageKey ?? null }}
        after={{ heroImageKey: pendingHeroKey }}
        onCancel={() => setPendingHeroKey(null)}
        onConfirm={(changelog) => {
          const key = pendingHeroKey;
          setPendingHeroKey(null);
          if (key) void commitHero(key, changelog);
        }}
      />

      <WhatsChangedModal
        open={pendingOrderIds !== null}
        kind="courses"
        title={course?.title ?? "course"}
        before={orderSnapshot(orderBeforeIds)}
        after={orderSnapshot(pendingOrderIds ?? [])}
        saving={orderSaving}
        onCancel={cancelPendingOrder}
        onConfirm={(changelog) => void commitOrder(changelog)}
      />

      <WhatsChangedModal
        open={pendingDetails !== null}
        kind="courses"
        title={course?.title ?? "course"}
        before={{
          title: course?.title ?? "",
          description: course?.description ?? "",
        }}
        after={{
          title: pendingDetails?.title ?? "",
          description: pendingDetails?.description ?? "",
        }}
        saving={detailsSaving}
        onCancel={() => setPendingDetails(null)}
        onConfirm={(changelog) => void commitDetails(changelog)}
      />

      <InspirationModal
        open={inspiration !== null}
        noun="course"
        info={inspiration?.info ?? null}
        loading={inspiration?.loading}
        upgrading={inspiration?.upgrading}
        onClose={() => setInspiration(null)}
        onUpgrade={() => void handleUpgrade()}
        onFindAuthor={(email) =>
          navigate(`/teacher/courses?tab=community&q=${encodeURIComponent(`author:${email}`)}`)
        }
      />
    </div>
  );
}
